import { LegacyShapedAssociatedEntity as AssociatedEntity } from '@palmetto/associated-entity';
import { RequestInterval } from '../energyAdvisor/types';

export interface Property {
  _id: string;
  address: Address;
  building?: HeliosBuilding;
  associatedEntities?: AssociatedEntity[];
  hasEnergyAdvisorData?: boolean;
  hasGridConnection?: boolean;
  electricUtility?: string;
  timeZone?: string;
  sundialSite?: SundialSite;
  // TODO: Remove solarSystemActivationDate after backfill script has run for all properties
  solarSystemActivationDate?: string;
}

export enum SundialEnergyConnectionTypes {
  gridToHome = 'gridToHome',
  gridToBattery = 'gridToBattery',
  pvToHome = 'pvToHome',
  batteryToHome = 'batteryToHome',
  batteryToGrid = 'batteryToGrid',
  pvToBattery = 'pvToBattery',
  pvToGrid = 'pvToGrid',
}

export enum SourceService {
  arcadia = 'arcadia',
  enphase = 'enphase',
  solaredge = 'solaredge',
  tesla = 'tesla',
  tigo = 'tigo',
  generac = 'generac',
}

interface DataSource {
  sourceType: EnergyType | BatteryType;
  sourceService: SourceService;
  sourceId: string;
}

export enum EnergyType {
  production = 'production',
  consumption = 'consumption',
  storage = 'storage',
  gridConsumption = 'gridConsumption',
}

export enum BatteryType {
  acb = 'acb',
  encharge = 'encharge',
}

export interface SundialSite {
  // id, zip, createdAt and currentStatus are declared on the type for historical reasons, but
  // GET /properties/:id has never actually populated them (v1 didn't either) — no consumer reads them.
  id?: string;
  timeZone: string;
  zip?: string;
  dataSources: DataSource[];
  createdAt?: string;
  connections: SundialEnergyConnectionTypes[];
  currentStatus?: string;
  earliestReadingTimestamp?: string;
  mostRecentReadingTimestamp?: string;
  firstMeasurableGenerationTimestamp?: string;
}

export type Address = {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  coordinates?: Coordinates;
};

export type Coordinates = {
  lat: number;
  lng: number;
};

export const BUILDING_ATTRIBUTES = [
  'airConditioning',
  'baseElectricityRate',
  'baseNaturalGasRateCcf',
  'buildingType',
  'electricVehicle',
  'floorAreaSqFt',
  'heatingType',
  'heatingSystemAgeInYears',
  'hasSolar',
  'hasGenerator',
  'hasFrequentBlackouts',
  'hasTanklessWaterHeater',
  'waterHeaterAgeInYears',
  'insulation',
  'lighting',
  'numOccupants',
  'numStories',
  'pool',
  'rangeOvenHeatingType',
  'roofAgeInYears',
  'roofType',
  'thermostat',
  'waterHeaterTempInFahrenheit',
  'waterHeatingType',
  'windows',
  'yearBuilt',
  'hvacHeatingEquipment',
  'hasCoolOrHotZones',
  'hvacIsSeasonallyExpensive',
  'hvacHasLongHeatUpAndCoolDownTimes',
  'isDrafty',
  'hvacIsNoisy',
  'hvacIsExpensiveToRepair',
  'tariffId',
] as const;

export type BuildingAttribute = (typeof BUILDING_ATTRIBUTES)[number];

export const BUILDING_ATTRIBUTE_GROUPS = ['hvacIssues'] as const;
export type BuildingAttributeGroup = (typeof BUILDING_ATTRIBUTE_GROUPS)[number];

export const BUILDING_ATTRIBUTE_GROUP_MEMBERS: Record<BuildingAttributeGroup, BuildingAttribute[]> = {
  hvacIssues: [
    'hasCoolOrHotZones',
    'hvacIsSeasonallyExpensive',
    'hvacHasLongHeatUpAndCoolDownTimes',
    'isDrafty',
    'hvacIsNoisy',
    'hvacIsExpensiveToRepair',
  ],
};

export type BuildingAttributeHistoryItemType = 'estimate' | 'confirmedEstimate' | 'userInput';

export const COMPLETED_BUILDING_ATTRIBUTES_TYPES = new Set(['confirmedEstimate', 'userInput']);

export interface BuildingAttributeHistoryItem {
  value: string | number;
  createdAt: Date;
  type: BuildingAttributeHistoryItemType;
}

export type HeliosBuilding = Partial<Record<BuildingAttribute, BuildingAttributeHistoryItem>>;

export enum BuildingType {
  singleFamily = 'single_family',
  multiFamily = 'multi_family',
}

export enum FuelType {
  electric = 'electric',
  fossilFuel = 'fossil_fuel',
}

export enum InsulationType {
  standard = 'standard',
  efficient = 'efficient',
  uninsulated = 'uninsulated',
}

export enum InsulationRValues {
  r7 = 'R-7',
  r11 = 'R-11',
  r13 = 'R-13',
  r15 = 'R-15',
  r19 = 'R-19',
}

export enum WindowEfficiency {
  standard = 'standard',
  efficient = 'efficient',
}

export interface UtilityRateMeta {
  type?: string;
  eiaId?: number;
  rateClass?: string;
  utilityName?: string;
  serviceType?: string;
  sampleId?: string;
  sampleName?: string;
}

export interface UtilityFlatRate {
  value: number;
  units: UtilityFlatRateUnits;
  meta?: UtilityRateMeta;
}

export enum UtilityFlatRateUnits {
  kwh = '$/kWh',
  ccf = '$/ccf',
}

export interface UtilityRateInfo {
  electric?: UtilityFlatRate;
  naturalGas?: UtilityFlatRate;
}

export interface ApiBuildingAttributeUpdateItem {
  buildingAttribute: BuildingAttribute;
  value: number | string;
  type: Omit<BuildingAttributeHistoryItemType, 'estimate'>;
}

export interface ApiBuildingAttributesUpdateRequest {
  buildingAttributes: ApiBuildingAttributeUpdateItem[];
}

export interface UpdateBuildingAttributes extends ApiBuildingAttributesUpdateRequest {
  propertyId: string;
}

export interface LifetimeImpactApiResponse {
  productionInKwh: number;
  offsetInKwh?: number;
  totalSavingsInDollars?: number;
  totalSolarValueInDollars?: number;
  dailySavings?: DailySavings[];
}

interface DailySavings {
  date: Date;
  savingsInDollars: number;
  projectedSavingsInDollars?: number;
}

export interface PropertyRequestParams {
  interval?: RequestInterval;
  buildingAttributeHash?: string;
}

export type HeatingType = 'electric' | 'fossil_fuel' | 'natural_gas' | 'heating_oil' | 'propane';

export type HvacHeatingEquipment =
  | 'Electric Resistance (Furnace, Baseboard)'
  | 'Air Sourced Heat Pump (Ducted)'
  | 'Mini-Split Heat Pump (Non Ducted)'
  | 'Geo-thermal Heat Pump'
  | 'Furnace (Natural Gas)'
  | 'Boiler (Natural Gas)'
  | 'Furnace (Fuel Oil)'
  | 'Boiler (Fuel Oil)'
  | 'Furnace (Propane)'
  | 'Boiler (Propane)';

export interface Utility {
  _id: string;
  name: string;
  lseId: string;
  eiaId: string;
  utilityConnectionAvailable?: boolean;
  arcadiaProviderId?: string;
}

export type PropertyPatchRequest = Partial<Pick<Property, 'electricUtility' | 'address'>>;

export type TariffServiceType = 'ELECTRICITY' | 'SOLAR_PV';

export interface PublicTariff {
  masterTariffId: string;
  tariffName: string;
  tariffCode?: string;
  serviceType?: TariffServiceType;
}
